import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import slugify from "slugify";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";
import RichTextEditor from "../../RichTextEditor";

// Convert Bengali digits to English digits
function convertBengaliToEnglish(str) {
  const map = {
    "০": "0",
    "১": "1",
    "২": "2",
    "৩": "3",
    "৪": "4",
    "৫": "5",
    "৬": "6",
    "৭": "7",
    "৮": "8",
    "৯": "9",
  };
  return str.replace(/[০-৯]/g, (digit) => map[digit]);
}

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const EditDepartment = ({ isSearchable }) => {
  const { id } = useParams(); // department id from URL
  const { t, i18n } = useTranslation([]);
  const navigate = useNavigate();
  const editor = useRef(null);

  // Department data shape
  const [departmentData, setDepartmentData] = useState({
    translations: {
      en: { name: "", description: "", metaTitle: "", metaDescription: "" },
      bn: { name: "", description: "", metaTitle: "", metaDescription: "" },
    },
    icon: null,
    slug: "",
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Icon file and preview
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  // Fetch existing department data
  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        setIsLoading(true);
        // console.log(`Fetching department data for lang=${i18n.language}`); // Debug
        const response = await axios.get(
          `https://api.muktihospital.com/api/department/${id}?lang=${i18n.language}`,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          }
        );

        if (response.status === 200) {
          const dep = response.data;
          // console.log("Fetched data:", dep); // Debug
          setDepartmentData((prev) => ({
            ...prev,
            translations: {
              ...prev.translations,
              [i18n.language]: {
                name: dep.translations.name || "",
                description: dep.translations.description || "",
                metaTitle: dep.translations.metaTitle || "",
                metaDescription: dep.translations.metaDescription || "",
              },
            },
            icon: dep.icon || null,
            slug: dep.slug || "",
          }));
        } else {
          toast.error("Failed to load department details.");
        }
      } catch (error) {
        console.error("Error fetching department:", error);
        toast.error("Could not load department.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDepartment();
  }, [id, i18n.language]);

  // Language Switch (English <-> Bangla)
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  const handleLanguageChange = (selectedOption) => {
    // console.log("Switching language to:", selectedOption.value); // Debug
    i18n.changeLanguage(selectedOption.value);
  };

  // Input field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const convertedValue = convertBengaliToEnglish(value);

    setDepartmentData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [i18n.language]: {
          ...prev.translations[i18n.language],
          [name]: convertedValue,
        },
      },
    }));

    // Auto-generate slug if the English department name is modified
    if (i18n.language === "en" && name === "name") {
      const generatedSlug = slugify(convertedValue, { lower: true, strict: true });
      setDepartmentData((prev) => ({
        ...prev,
        slug: prev.slug || generatedSlug,
      }));
    }
  };

  // Slug manual change
  const handleSlugChange = (e) => {
    const slugValue = e.target.value;
    setDepartmentData((prev) => ({
      ...prev,
      slug: slugify(slugValue, { lower: true, strict: true }),
    }));
  };

  // File input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setIconFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setIconPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setIconPreview(null);
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      // Send translations for the current language
      formData.append(
        "translations",
        JSON.stringify({
          [i18n.language]: departmentData.translations[i18n.language],
        })
      );

      if (iconFile) {
        formData.append("icon", iconFile);
      }

      formData.append("slug", departmentData.slug);

      // console.log("Submitting data:", Object.fromEntries(formData)); // Debug
      const response = await axios.put(
        `https://api.muktihospital.com/api/department/${id}`,
        formData,
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Department updated successfully!");
        navigate("/all-department");
      } else {
        toast.error("Failed to update department.");
      }
    } catch (error) {
      console.error("Error updating department:", error);
      toast.error("An error occurred while updating the department.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContentChange = (newContent) => {
    setDepartmentData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [i18n.language]: {
          ...prev.translations[i18n.language],
          description: newContent,
        },
      },
    }));
  };

  // If loading
  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton height={30} count={6} />
      </div>
    );
  }

  const currentTranslation = departmentData.translations[i18n.language]?.description || "";

  // Render form
  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Department"},
    { label: "Edit Department" },
  ];
  return (
    <div>
      <PageHeading title="Edit Department" breadcrumbs={breadcrumbs} />
      {/* Language switcher */}
      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === i18n.language)}
          isSearchable={isSearchable}
        />
      </div>
      <div className="min-h-screen">
        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="bg-white shadow-sm rounded-lg">
              <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Basic Information
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
                {/* Department Name */}
                <div>
                  <label className="label">Department Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter Department Name"
                    className="input-field"
                    disabled={isSubmitting}
                    value={departmentData.translations[i18n.language]?.name || ""}
                    onChange={handleInputChange}
                  />
                </div>
                {/* Slug Field */}
                <div>
                  <label className="label">Slug</label>
                  <input
                    type="text"
                    name="slug"
                    className="input-field"
                    placeholder="Enter Slug"
                    value={departmentData.slug}
                    onChange={handleSlugChange}
                  />
                </div>
              </div>
              {/* Department Description */}
              <div className="p-5">
                <label className="label">Department Description</label>
                {isSubmitting ? (
                  <Skeleton height={35} />
                ) : (
                  <RichTextEditor
                    value={currentTranslation}
                    onChange={handleContentChange}
                  />
                )}
              </div>
            </div>
            <div>
              <div className="bg-white shadow-sm rounded-lg">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    SEO Configuration
                  </h2>
                </div>
                <div className="p-5 grid grid-cols-2 gap-6">
                  {/* Meta Title */}
                  <div>
                    <label className="label">Meta Title</label>
                    {isSubmitting ? (
                      <Skeleton height={35} />
                    ) : (
                      <input
                        type="text"
                        name="metaTitle"
                        placeholder="Enter Meta Title (SEO)"
                        className="input-field"
                        value={
                          departmentData.translations[i18n.language]?.metaTitle || ""
                        }
                        onChange={handleInputChange}
                      />
                    )}
                  </div>
                  {/* Meta Description */}
                  <div>
                    <label className="label">Meta Description</label>
                    {isSubmitting ? (
                      <Skeleton height={50} />
                    ) : (
                      <input
                        name="metaDescription"
                        placeholder="Enter Meta Description (SEO)"
                        className="input-field"
                        value={
                          departmentData.translations[i18n.language]?.metaDescription || ""
                        }
                        onChange={handleInputChange}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-lg p-4 mt-5">
                {/* New icon preview */}
                {iconPreview && (
                  <div className="mt-6">
                    <label className="block mb-1 font-medium">New Icon Preview</label>
                    <img
                      src={iconPreview}
                      alt="New Icon"
                      className="w-20 h-20 object-cover rounded"
                    />
                  </div>
                )}
                {/* Existing icon preview */}
                {departmentData.icon && !iconPreview && (
                  <div className="mt-6">
                    <img
                      src={`https://api.muktihospital.com${departmentData.icon}`}
                      alt="Dept Icon"
                      className="w-20 h-20 object-cover rounded"
                    />
                  </div>
                )}
                {/* File input */}
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mt-1">
                    Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    className="border p-2 rounded w-full"
                    disabled={isSubmitting}
                    onChange={handleFileChange}
                  />
                </div>
              </div>
              {/* Submit Button */}
              <div className="mt-6 flex gap-4 justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                >
                  {isSubmitting ? "Updating..." : "Update Department"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDepartment;